import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { getDealByIdAsync as getDealById, getDealsAsync as getDefaultDeals, getNacharbeitenStatusAsync as getNacharbeitenStatus, searchDealsAsync as searchDeals } from '../services/api/hubspot';
import { HubSpotDeal } from '../types/hubspot';

export type { DealsNacharbeitenFilter } from '../types/filters';
import type { DealsNacharbeitenFilter } from '../types/filters';
export type { FetchDealsOptions, DealsState } from '../interfaces/deals';
import type { DealsState } from '../interfaces/deals';

const DEALS_PAGE_SIZE = Number(import.meta.env.VITE_DEALS_PAGE_SIZE || 100);
const DEALS_BACKGROUND_DELAY_MS = 1000;
const DEALS_SEARCH_ENRICH_CONCURRENCY = 4;
const MOBILE_DEALS_HARD_LIMIT = Number(import.meta.env.VITE_MOBILE_DEALS_HARD_LIMIT || 800);
const PERSISTED_DEALS_LIMIT = Number(import.meta.env.VITE_PERSISTED_DEALS_LIMIT || 120);

let backgroundSyncPromise: Promise<void> | null = null;
let backgroundSyncRunId = 0;
let activeInitialFetchController: AbortController | null = null;
let activeBackgroundPageController: AbortController | null = null;
let activeSearchController: AbortController | null = null;
let searchRunId = 0;
let unfilteredDealsCache: HubSpotDeal[] = [];
let unfilteredHasMoreCache = false;
let unfilteredLastFetchedCache: string | null = null;
let unfilteredAfterCache: string | undefined = undefined;

function isMobileViewport(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(max-width: 639px)').matches;
}

function trimDealsForDevice(deals: HubSpotDeal[]): HubSpotDeal[] {
  if (!isMobileViewport()) return deals;
  if (deals.length <= MOBILE_DEALS_HARD_LIMIT) return deals;
  return deals.slice(0, MOBILE_DEALS_HARD_LIMIT);
}

function isQuotaExceededError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as any)?.name;
  const code = (error as any)?.code;
  const message = String((error as any)?.message || '').toLowerCase();
  return (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014 ||
    message.includes('quota') ||
    message.includes('exceeded')
  );
}

function createSafeStorage() {
  const memoryStorage = new Map<string, string>();
  const safeMemory = {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStorage.set(key, value);
    },
    removeItem: (key: string) => {
      memoryStorage.delete(key);
    },
  };

  if (typeof window === 'undefined' || !window.localStorage) {
    return safeMemory;
  }

  return {
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return memoryStorage.get(key) ?? null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        if (isQuotaExceededError(error)) {
          console.warn('[dealsStore] localStorage quota exceeded, skipping persist write.');
          return;
        }
        console.warn('[dealsStore] localStorage write failed, skipping persist write.', error);
      }
    },
    removeItem: (key: string) => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        memoryStorage.delete(key);
      }
    },
  };
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function mergeDeals(existing: HubSpotDeal[], incoming: HubSpotDeal[]): HubSpotDeal[] {
  const byId = new Map(existing.map((deal) => [deal.id, deal]));
  incoming.forEach((deal) => {
    const current = byId.get(deal.id);
    if (!current) {
      byId.set(deal.id, deal);
      return;
    }

    byId.set(deal.id, {
      ...current,
      ...deal,
      project_status: deal.project_status ?? current.project_status,
    });
  });
  return Array.from(byId.values());
}

function isAbortLikeError(error: unknown): boolean {
  const message = error instanceof Error ? error.message.toLowerCase() : String(error || '').toLowerCase();
  return message.includes('abort') || message.includes('canceled') || message.includes('cancelled');
}

function isAuthMissingError(error: unknown): boolean {
  return !!(error && typeof error === 'object' && (error as any).isAuthMissing);
}

function buildRequestKey(options: {
  query: string;
  stageLabel: string;
  nacharbeiten: DealsNacharbeitenFilter;
}) {
  return JSON.stringify(options);
}

function localDealMatchesSearch(deal: HubSpotDeal, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    deal.id,
    deal.dealname,
    deal.dealstage_label || deal.dealstage,
    deal.objekt___anschrift,
    deal.deal_owner_name,
    deal.verantwortlicher_monteur_name,
  ]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
  return haystack.includes(q);
}

function isUnfilteredRequest(params: {
  query: string;
  stageLabel: string;
  nacharbeiten: DealsNacharbeitenFilter;
}): boolean {
  return params.query.length === 0 && params.stageLabel.length === 0 && params.nacharbeiten === 'all';
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];
  const normalizedConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: normalizedConcurrency }, async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex]);
    }
  });

  await Promise.all(workers);
  return results;
}

function injectNacharbeiten(deal: HubSpotDeal, nacharbeiten: any): HubSpotDeal {
  if (!nacharbeiten) return deal;
  if (!deal.project_status) return deal;
  return {
    ...deal,
    project_status: {
      ...deal.project_status,
      nacharbeiten,
    },
  };
}

async function enrichDealsWithStatusAndNacharbeiten(
  deals: HubSpotDeal[],
  signal?: AbortSignal
): Promise<HubSpotDeal[]> {
  if (deals.length === 0) return deals;

  const enriched = await mapWithConcurrency(deals, DEALS_SEARCH_ENRICH_CONCURRENCY, async (deal) => {
    if (signal?.aborted) return deal;

    const [freshDeal, nacharbeiten] = await Promise.all([
      getDealById(deal.id).catch(() => null),
      getNacharbeitenStatus(deal.id).catch(() => null),
    ]);

    const merged = freshDeal ? mergeDeals([deal], [freshDeal])[0] : deal;
    return injectNacharbeiten(merged, nacharbeiten);
  });

  return enriched;
}

export const useDealsStore = create<DealsState>()(
  persist(
    (set, get) => ({
      deals: [],
      searchResults: null,
      lastFetched: null,
      afterCursor: null,
      cacheOwnerKey: null,
      activeRequestKey: null,
      isLoading: false,
      isLoadingMore: false,
      hasMore: false,
      error: null,
      loadMoreError: null,
      isSearching: false,
      searchError: null,

      fetchDeals: async (options = {}) => {
        const query = (options.query || '').trim();
        const stageLabel = (options.stageLabel || '').trim();
        const nacharbeiten = options.nacharbeiten || 'all';
        const requestKey = buildRequestKey({ query, stageLabel, nacharbeiten });
        const requestIsUnfiltered = isUnfilteredRequest({ query, stageLabel, nacharbeiten });

        const invalidateFetchRun = () => {
          backgroundSyncRunId += 1;
          activeBackgroundPageController?.abort();
          activeBackgroundPageController = null;
          activeInitialFetchController?.abort();
          activeInitialFetchController = null;
          // Sofort freigeben, damit ein neuer 100er-Background-Run direkt starten kann.
          backgroundSyncPromise = null;
          // Laufende Such-Enrichments ebenfalls abbrechen, damit Refresh nicht blockiert.
          searchRunId += 1;
          activeSearchController?.abort();
          activeSearchController = null;
        };

        const startBackgroundSync = (
          params: { query: string; stageLabel: string; nacharbeiten: DealsNacharbeitenFilter },
          initialAfter?: string,
          bypassCache?: boolean
        ) => {
          if (!initialAfter) return;
          if (backgroundSyncPromise) return;
          const runId = backgroundSyncRunId;
          backgroundSyncPromise = (async () => {
            let nextAfterCursor: string | undefined = initialAfter;
            try {
              while (nextAfterCursor && (get().hasMore)) {
                if (runId !== backgroundSyncRunId) break;
                if (!navigator.onLine) break;
                await delay(DEALS_BACKGROUND_DELAY_MS);
                if (runId !== backgroundSyncRunId) break;
                set({ isLoadingMore: true });
                activeBackgroundPageController?.abort();
                activeBackgroundPageController = new AbortController();
                const nextPage = await getDefaultDeals({
                  after: nextAfterCursor,
                  bypassCache,
                  limit: DEALS_PAGE_SIZE,
                  q: params.query || undefined,
                  stageLabel: params.stageLabel || undefined,
                  nacharbeiten: params.nacharbeiten,
                  signal: activeBackgroundPageController.signal,
                });
                const nextDeals = nextPage.deals || [];
                nextAfterCursor = nextPage.after || undefined;
                set((state) => {
                  const mergedDeals = mergeDeals(state.deals, nextDeals);
                  const trimmedDeals = trimDealsForDevice(mergedDeals);
                  const reachedDeviceLimit = trimmedDeals.length < mergedDeals.length;
                  const nextHasMore = !reachedDeviceLimit && Boolean(nextPage.hasMore) && !!nextPage.after;
                  const nowIso = new Date().toISOString();

                  if (isUnfilteredRequest(params)) {
                    unfilteredDealsCache = trimmedDeals;
                    unfilteredHasMoreCache = nextHasMore;
                    unfilteredLastFetchedCache = nowIso;
                    unfilteredAfterCache = nextAfterCursor;
                  }

                  return {
                    deals: trimmedDeals,
                    hasMore: nextHasMore,
                    afterCursor: nextAfterCursor || null,
                    lastFetched: nowIso,
                    isLoadingMore: false,
                  };
                });
                if (!nextAfterCursor || nextDeals.length === 0) break;
              }
            } catch (err: any) {
              if (isAbortLikeError(err) || isAuthMissingError(err)) {
                set({ isLoadingMore: false });
                return;
              }
              set({
                loadMoreError: err.message || 'Weitere Deals konnten nicht nachgeladen werden.',
                isLoadingMore: false,
              });
            } finally {
              activeBackgroundPageController = null;
              backgroundSyncPromise = null;
            }
          })();
        };

        const { deals, lastFetched, isLoading, cacheOwnerKey: existingCacheOwnerKey, activeRequestKey } = get();
        if (isLoading) return;

        const nextCacheOwnerKey = options.cacheOwnerKey || existingCacheOwnerKey || null;
        const sameOwner =
          !existingCacheOwnerKey ||
          !nextCacheOwnerKey ||
          existingCacheOwnerKey === nextCacheOwnerKey;
        const sameRequest = !activeRequestKey || activeRequestKey === requestKey;

        if (options.restoreUnfilteredFromCache && requestIsUnfiltered && unfilteredDealsCache.length > 0) {
          const cursor = unfilteredAfterCache || get().afterCursor || undefined;
          set({
            deals: unfilteredDealsCache,
            hasMore: unfilteredHasMoreCache,
            lastFetched: unfilteredLastFetchedCache || new Date().toISOString(),
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            isLoading: false,
            isLoadingMore: false,
            error: null,
            loadMoreError: null,
          });
          if (unfilteredHasMoreCache) {
            startBackgroundSync({ query, stageLabel, nacharbeiten }, cursor);
          }
          return;
        }

        const storedCursor = unfilteredAfterCache || get().afterCursor || undefined;
        const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        const canUseCachedDeals = isOffline
          && !options.forceRefresh && !options.refresh && !options.refreshNacharbeiten
          && sameOwner && sameRequest && deals.length > 0 && lastFetched
          && (!get().hasMore || !!storedCursor);
        if (canUseCachedDeals) {
          set({
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            error: null,
            loadMoreError: null,
          });
          return;
        }

        invalidateFetchRun();

        if (options.forceRefresh || !sameRequest) {
          set({
            deals: [],
            searchResults: null,
            hasMore: false,
            lastFetched: null,
            error: null,
            loadMoreError: null,
          });
        }

        set({
          isLoading: true,
          isLoadingMore: false,
          activeRequestKey: requestKey,
          error: null,
          loadMoreError: null,
        });
        try {
          activeInitialFetchController = new AbortController();
          const data = await getDefaultDeals({
            refresh: options.refresh || options.forceRefresh,
            refreshNacharbeiten: options.refreshNacharbeiten,
            limit: DEALS_PAGE_SIZE,
            q: query || undefined,
            stageLabel: stageLabel || undefined,
            nacharbeiten,
            signal: options.signal || activeInitialFetchController.signal,
          });
          const firstPageDeals = data.deals || [];
          const trimmedFirstPageDeals = trimDealsForDevice(firstPageDeals);
          const reachedDeviceLimit = trimmedFirstPageDeals.length < firstPageDeals.length;
          const nowIso = new Date().toISOString();

          if (requestIsUnfiltered) {
            unfilteredDealsCache = trimmedFirstPageDeals;
            unfilteredHasMoreCache = !reachedDeviceLimit && Boolean(data.hasMore);
            unfilteredLastFetchedCache = nowIso;
            unfilteredAfterCache = data.after || undefined;
          }

          set(() => ({
            deals: trimmedFirstPageDeals,
            searchResults: query.length >= 3 ? trimmedFirstPageDeals : null,
            lastFetched: nowIso,
            afterCursor: data.after || null,
            cacheOwnerKey: nextCacheOwnerKey,
            activeRequestKey: requestKey,
            hasMore: !reachedDeviceLimit && Boolean(data.hasMore),
            isLoading: false,
          }));
          if (data.hasMore && !reachedDeviceLimit) {
            const isRefresh = !!(options.refresh || options.forceRefresh);
            startBackgroundSync({ query, stageLabel, nacharbeiten }, data.after || undefined, isRefresh);
          }
        } catch (err: any) {
          if (isAbortLikeError(err) || isAuthMissingError(err)) {
            set({
              isLoading: false,
              isLoadingMore: false,
            });
            return;
          }
          set({
            error: err.message || 'Fehler beim Laden der Deals',
            isLoading: false,
            isLoadingMore: false,
          });
        } finally {
          activeInitialFetchController = null;
        }
      },

      searchDealsIntoCache: async (query: string, options = {}) => {
        void options;
        const q = query.trim();
        if (q.length < 3) return;

        searchRunId += 1;
        const currentSearchRunId = searchRunId;
        activeSearchController?.abort();
        activeSearchController = new AbortController();

        const currentDeals = get().deals;
        const localMatches = currentDeals.filter((deal) => localDealMatchesSearch(deal, q));

        set({ isSearching: true, searchError: null });
        set({
          searchResults: localMatches,
          hasMore: false,
          activeRequestKey: buildRequestKey({ query: q, stageLabel: '', nacharbeiten: 'all' }),
        });

        try {
          const enrichedLocal = await enrichDealsWithStatusAndNacharbeiten(localMatches, activeSearchController.signal);
          if (currentSearchRunId !== searchRunId) return;
          set((state) => ({
            searchResults: mergeDeals(state.searchResults || [], enrichedLocal),
            deals: mergeDeals(state.deals, enrichedLocal),
          }));

          const serverMatches = await searchDeals(q, { signal: activeSearchController.signal });
          if (currentSearchRunId !== searchRunId) return;
          const serverEnriched = await enrichDealsWithStatusAndNacharbeiten(serverMatches || [], activeSearchController.signal);
          if (currentSearchRunId !== searchRunId) return;
          const merged = mergeDeals(enrichedLocal, serverEnriched || []);
          set({
            searchResults: merged,
            deals: mergeDeals(get().deals, merged),
            lastFetched: new Date().toISOString(),
            hasMore: false,
            isSearching: false,
          });
        } catch (err: any) {
          if (isAbortLikeError(err) || isAuthMissingError(err)) {
            if (currentSearchRunId === searchRunId) {
              set({ isSearching: false });
            }
            return;
          }
          if (currentSearchRunId !== searchRunId) return;
          set({
            searchError: err.message || 'HubSpot-Suche fehlgeschlagen.',
            isSearching: false,
          });
        } finally {
          if (currentSearchRunId === searchRunId) {
            activeSearchController = null;
          }
        }
      },

      upsertDeal: (deal: HubSpotDeal) => {
        // Keep the unfiltered in-memory cache in sync so restoreUnfilteredFromCache
        // does not resurrect stale deal status after local updates.
        unfilteredDealsCache = mergeDeals(unfilteredDealsCache, [deal]);
        unfilteredLastFetchedCache = new Date().toISOString();
        set((state) => ({
          deals: mergeDeals(state.deals, [deal]),
          searchResults: state.searchResults ? mergeDeals(state.searchResults, [deal]) : state.searchResults,
          lastFetched: new Date().toISOString(),
        }));
      },

      clearLoadMoreError: () => set({ loadMoreError: null }),
    }),
    {
      name: '360volt-deals-store',
      version: 2,
      storage: createJSONStorage(createSafeStorage),
      partialize: (state) => ({
        deals: state.deals.slice(0, PERSISTED_DEALS_LIMIT),
        lastFetched: state.lastFetched,
        cacheOwnerKey: state.cacheOwnerKey,
        hasMore: state.hasMore,
        activeRequestKey: state.activeRequestKey,
        afterCursor: state.afterCursor,
      }),
    }
  )
);
